import { Button } from '@/components/ui/Button'
import { X, Volume2, VolumeX, Mic, Settings } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface VoiceSettingsModalProps {
  isOpen: boolean
  onClose: () => void
  // TTS Settings (Browser text-to-speech) - Optional for OpenAI Realtime mode
  isTtsEnabled?: boolean
  onToggleTts?: () => void
  availableVoices?: SpeechSynthesisVoice[]
  selectedVoice?: string
  onVoiceChange?: (voice: string) => void
  voiceSpeed?: number
  onVoiceSpeedChange?: (speed: number) => void
  // Voice Input Settings (OpenAI Realtime)
  isVoiceListening: boolean
  onVoiceToggle: () => void
  isVoiceSpeaking: boolean
  onStopSpeaking: () => void
  currentTranscript?: string
  voiceError?: string | null
  onClearVoiceError?: () => void
  // OpenAI Realtime Voice Selection
  realtimeVoice?: string
  onRealtimeVoiceChange?: (voice: string) => void
  // OpenAI Realtime Mute Control
  isMuted?: boolean
  onToggleMute?: () => void
}

export function VoiceSettingsModal({
  isOpen,
  onClose,
  isTtsEnabled,
  onToggleTts,
  availableVoices,
  selectedVoice,
  onVoiceChange,
  voiceSpeed,
  onVoiceSpeedChange,
  isVoiceListening,
  onVoiceToggle,
  isVoiceSpeaking,
  onStopSpeaking,
  currentTranscript,
  voiceError,
  onClearVoiceError,
  realtimeVoice = 'alloy',
  onRealtimeVoiceChange,
  isMuted = false,
  onToggleMute
}: VoiceSettingsModalProps) {
  const { t } = useTranslation()
  // OpenAI Realtime voice options
  const realtimeVoices = [
    { value: 'alloy', label: t('voice.settings.voices.alloy.label'), description: t('voice.settings.voices.alloy.desc') },
    { value: 'echo', label: t('voice.settings.voices.echo.label'), description: t('voice.settings.voices.echo.desc') },
    { value: 'fable', label: t('voice.settings.voices.fable.label'), description: t('voice.settings.voices.fable.desc') },
    { value: 'onyx', label: t('voice.settings.voices.onyx.label'), description: t('voice.settings.voices.onyx.desc') },
    { value: 'nova', label: t('voice.settings.voices.nova.label'), description: t('voice.settings.voices.nova.desc') },
    { value: 'shimmer', label: t('voice.settings.voices.shimmer.label'), description: t('voice.settings.voices.shimmer.desc') }
  ]

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Settings className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900 text-white">{t('voice.settings.title')}</h2>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="p-4 space-y-6">
          {/* Voice Input Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-900 flex items-center space-x-2 text-white">
              <Mic className="w-4 h-4" />
              <span>{t('voice.settings.conversationTitle')}</span>
            </h3>

            <div className="space-y-3">
              {/* Voice Input Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700">{t('voice.settings.conversationLabel')}</p>
                  <p className="text-xs text-gray-500">{t('voice.settings.conversationDesc')}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={isVoiceSpeaking ? onStopSpeaking : onVoiceToggle}
                  className={`${isVoiceListening
                      ? 'text-red-600 hover:bg-red-50'
                      : isVoiceSpeaking
                        ? 'text-green-600 hover:bg-green-50'
                        : 'text-gray-500 hover:bg-gray-50'
                    }`}
                >
                  <Mic className={`w-4 h-4 ${isVoiceListening || isVoiceSpeaking ? 'animate-pulse' : ''
                    }`} />
                </Button>
              </div>

              {/* OpenAI Realtime Voice Selection */}
              {onRealtimeVoiceChange && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">AI Voice</label>
                  <select
                    value={realtimeVoice}
                    onChange={(e) => onRealtimeVoiceChange(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {realtimeVoices.map((voice) => (
                      <option key={voice.value} value={voice.value}>
                        {voice.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500">
                    {realtimeVoices.find(v => v.value === realtimeVoice)?.description}
                  </p>
                </div>
              )}

              {/* Mute/Unmute Toggle */}
              {onToggleMute && (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-700">{t('voice.settings.speakerOutputLabel')}</p>
                    <p className="text-xs text-gray-500">
                      {isMuted ? t('voice.settings.mutedDesc') : t('voice.settings.playingDesc')}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onToggleMute}
                    className={`${isMuted
                        ? 'text-red-600 hover:bg-red-50 border-red-300'
                        : 'text-green-600 hover:bg-green-50 border-green-300'
                      }`}
                  >
                    {isMuted ? (
                      <>
                        <VolumeX className="w-4 h-4 mr-1" />
                        {t('voice.settings.unmute')}
                      </>
                    ) : (
                      <>
                        <Volume2 className="w-4 h-4 mr-1" />
                        {t('voice.settings.mute')}
                      </>
                    )}
                  </Button>
                </div>
              )}
              {/* Audio Test Button */}
              {onRealtimeVoiceChange && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm font-medium text-blue-900 mb-2">🔧 {t('voice.settings.audioTest')}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      // Test audio playback directly
                      console.log('[TEST-AUDIO] 🧪 Starting audio test...')
                      try {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        const AudioContext = window.AudioContext || (window as any).webkitAudioContext
                        const ctx = new AudioContext()
                        console.log('[TEST-AUDIO] Context state:', ctx.state)

                        // Create a simple 440Hz tone
                        const oscillator = ctx.createOscillator()
                        const gainNode = ctx.createGain()

                        oscillator.connect(gainNode)
                        gainNode.connect(ctx.destination)

                        oscillator.frequency.value = 440 // A4 note
                        gainNode.gain.value = 0.3 // 30% volume

                        oscillator.start()
                        console.log('[TEST-AUDIO] ▶️ Playing 440Hz tone for 0.5 seconds...')

                        setTimeout(() => {
                          oscillator.stop()
                          console.log('[TEST-AUDIO] ⏹️ Stopped')
                        }, 500)
                      } catch (error) {
                        console.error('[TEST-AUDIO] ❌ Test failed:', error)
                        alert('Audio test failed! Check console.')
                      }
                    }}
                    className="w-full text-blue-600 hover:bg-blue-100 border-blue-300"
                  >
                    {t('voice.settings.testSpeakers')}
                  </Button>
                  <p className="text-xs text-sky-300 mt-2">
                    {t('voice.settings.audioBlocked')}
                  </p>
                </div>
              )}

              {/* Current Transcript */}
              {currentTranscript && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-xs text-blue-600 font-medium mb-1">{t('voice.settings.listening')}</p>
                  <p className="text-sm text-blue-800">{currentTranscript}</p>
                </div>
              )}

              {/* Voice Error */}
              {voiceError && (
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-red-600">{voiceError}</p>
                    {onClearVoiceError && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={onClearVoiceError}
                        className="text-red-600 hover:bg-red-100 p-1"
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* TTS Section - Hide if using OpenAI Realtime */}
          {!onRealtimeVoiceChange && (
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-900 flex items-center space-x-2 text-white">
                <Volume2 className="w-4 h-4" />
                <span>{t('voice.settings.ttsTitle')}</span>
              </h3>

              <div className="space-y-4">
                {/* TTS Toggle */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-700">{t('voice.settings.ttsLabel')}</p>
                    <p className="text-xs text-gray-500">{t('voice.settings.ttsDesc')}</p>
                  </div>
                  {onToggleTts && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={onToggleTts}
                      className={isTtsEnabled ? 'text-blue-600 hover:bg-blue-50' : 'text-gray-500 hover:bg-gray-50'}
                    >
                      {isTtsEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                    </Button>
                  )}
                </div>

                {/* Voice Selection */}
                {isTtsEnabled && availableVoices && availableVoices.length > 0 && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">{t('voice.settings.voiceLabel')}</label>
                    <select
                      value={selectedVoice}
                      onChange={(e) => onVoiceChange?.(e.target.value)}
                      className="w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {availableVoices.map((voice) => (
                        <option key={voice.name} value={voice.name}>
                          {voice.name} {voice.lang ? `(${voice.lang})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Voice Speed */}
                {isTtsEnabled && voiceSpeed !== undefined && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      {t('voice.settings.speechSpeed', { speed: voiceSpeed.toFixed(1) })}
                    </label>
                    <input
                      type="range"
                      min="0.5"
                      max="2"
                      step="0.1"
                      value={voiceSpeed}
                      onChange={(e) => onVoiceSpeedChange?.(parseFloat(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                    />
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>0.5x</span>
                      <span>1.0x</span>
                      <span>2.0x</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="space-y-3 pt-4 border-t border-gray-200">
            <h3 className="text-sm font-medium text-gray-900 text-white">{t('voice.settings.quickActions')}</h3>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  onToggleTts?.()
                  onClose()
                }}
                className="text-xs"
              >
                {isTtsEnabled ? t('voice.settings.disableVoice') : t('voice.settings.enableVoice')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  onVoiceToggle()
                  onClose()
                }}
                className="text-xs"
              >
                {isVoiceListening ? t('voice.settings.stopListening') : t('voice.settings.startVoiceInput')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
